<template>
    <b-card header-tag="header" footer-tag="footer" class="mt-8">
        <template v-slot:header>
            <p class="mb-0" style="font-size: 21px">Listado de Pedidos a Proveedor</p>
        </template>
        <b-row class="pb-2">
            <b-col cols="12" md="12">
                <div class="text-right">
                    <b-button-group>
                        <!-- Botón Descargar Excel -->
                        <b-button variant="info" class="text-white mr-2" @click="downloadExcel">
                            <b-icon icon="download"></b-icon> Descargar Excel
                        </b-button>

                        <!-- Botón Sincronizar Pagos (visible solo si el usuario es 'master') -->
                        <b-button v-if="user.isMaster" variant="success" class="text-white" @click="synchronizeOrders">
                            <b-icon icon="arrow-counterclockwise"></b-icon> Sincronizar Pagos
                        </b-button>
                    </b-button-group>
                </div>
            </b-col>
        </b-row>
        <b-card-text>
            <b-table 
                responsive
                :fields="computedFields" 
                hover 
                :items="pedidos">
                <template v-slot:cell(id)="data">
                    {{ data.item.id }}
                </template>
                <template v-slot:cell(nombre_usuario)="data">
                    {{ data.item.nombre_usuario }}
                </template>
                <template v-slot:cell(created_at_formatted)="data">
                    {{ data.item.created_at_formatted }}
                </template>
                <template v-slot:cell(tracking_url)="data">
                    <b-button 
                        v-if="data.item.tracking_url" 
                        size="sm" 
                        variant="primary" 
                        @click="openTrackingInNewWindow(data.item.tracking_url)">
                        <b-icon icon="truck"></b-icon> Seguimiento
                    </b-button>
                </template>
                <template v-slot:cell(total_precio)="data">
                   $ {{numberFormat(data.item.total_precio) }}
                </template>
                <template class="text-center" v-slot:cell(servicio_logistico)="data">
                    <b-badge variant="info" v-if="data.item.servicio_logistico">
                        Si
                    </b-badge>
                </template>
                <template class="text-center" v-slot:cell(sale_order_supplier_id)="data">
                    <b>{{ data.item.sale_order_supplier_id }}</b>
                </template>
                <template class="text-center" v-slot:cell(bill_supplier_id)="data">
                    <b>{{ data.item.bill_supplier_id }}</b>
                </template>
                <template class="text-center" v-slot:cell(supplier_bill_amount)="data">
                    <span v-if="data.item.supplier_bill_amount > 0">
                        $ {{numberFormat(data.item.supplier_bill_amount) }}
                    </span>
                </template>
                <template class="text-center" v-slot:cell(bill_supplier_date_formatted)="data">
                    {{ data.item.bill_supplier_date_formatted }}
                </template>
                <template class="text-center" v-slot:cell(payment_state)="data">
                    <h5><b-badge v-if="data.item.payment_state" :variant="statusClass[data.item.payment_state]">
                        {{ statusLabel[data.item.payment_state] }}
                    </b-badge></h5>
                </template>
                <template class="text-center" v-slot:cell(invoice_date_due_formatted)="data">
                    <b>{{ data.item.invoice_date_due_formatted }}</b>
                </template>
                <template v-slot:cell(actions)="{ item }">
                    <b-button-group>
                        <b-button size="sm" variant="info" v-b-tooltip.hover title="Ver pedido" class="text-white mr-2" 
                            :to="{name: 'InfoPedidoProveedor', params:{ id: item.id}}">
                            <b-icon icon="search"></b-icon>
                        </b-button>
                    </b-button-group>
                </template>
            </b-table>
            <template v-if="pedidos.length < 1">
                <b-alert show variant="danger">
                    Lo sentimos, no se han encontrado resultados para su busqueda.
                </b-alert>
            </template>
        </b-card-text>
        <template v-slot:footer>
            <div class="mt-3">
                <b-row>                            
                    <b-col cols="12" md="4">
                        <b-pagination 
                            size="md" 
                            pills 
                            :total-rows="paginator.totalPage" 
                            v-model="paginator.page" 
                            :per-page="1" 
                            @input="changePage" 
                            first-number 
                            last-number>
                        </b-pagination>
                    </b-col>
                    <b-col cols="12" sm="2">
                        <b-form-select @change="updatePageSize()" v-model="paginator.pageSize" :options="pageSize" ></b-form-select>
                    </b-col>
                </b-row>
            </div>
        </template>
    </b-card>
</template>

<script>
import { mapMutations, mapState } from 'vuex'

export default {
    name: "PedidosTable",
    props: {
        pedidos: {
            type: Array
        },
        paginator: {
            type: Object
        },
        pageSize: {
            type: Array
        }
    },
    computed: {
        computedFields() {
            // Crear una copia del array fields para manipularlo
            let fieldsToShow = [...this.fields];
            
            // Filtrar campos que requieren permisos especiales
            fieldsToShow = fieldsToShow.filter((field) => {
                if (field.requiresPermission) {
                    if (field.key === "tracking_url") {
                        return this.user.isMaster === 1;
                    }
                } else {
                    return true;
                }
            });
            
            return fieldsToShow;
        },
        ...mapState('auth', ['user']),
    },
    data(){
        return{
            fields: [
                { key: 'id', label: '#', sortable: true},
                { key: 'nombre_usuario', label: 'Realizado Por', sortable: true},
                { key: 'created_at_formatted', label: 'Fecha', sortable: true},
                { key: 'tracking_url', label: 'Tracking', sortable: true, requiresPermission: true},
                { key: 'total_precio', label: 'Precio Total', sortable: true},
                { key: 'servicio_logistico', label: 'Serv. Log', sortable: true},
                { key: 'sale_order_supplier_id', label: 'Orden de venta', sortable: true},
                { key: 'bill_supplier_id', label: 'Factura', sortable: true},
                { key: 'supplier_bill_amount', label: 'Monto Factura', sortable: true},
                { key: 'bill_supplier_date_formatted', label: 'Fecha Factura', sortable: true},
                { key: 'payment_state', label: 'Estado de Pago', sortable: true},
                { key: 'invoice_date_due_formatted', label: 'Fecha limite pago', sortable: true},
                { key: 'actions', label: 'Acciones'},
            ],
            statusLabel: {
                not_paid: "Sin pagar",
                in_payment: "En proceso de pago",
                paid: "Pagado",
                partial : "Pagado parcialmente",
                reversed: "Revertido",
            },
            statusClass: {
                not_paid: "danger",
                in_payment: "warning",
                paid: "success",
                partial : "warning",
                reversed: "success",
            },
            // Variables para el modal de tracking
            showTrackingModal: false,
            currentTrackingUrl: '',
        }
    },
    methods: {
        ...mapMutations(['setLoading']),
        updatePageSize(){
            /* the page sizee return the current page to initial because we can't know if the current page have items asigned!! */
            this.paginator.page = 1;
            /* emit the page size update to get the new list of items*/
            this.$emit('get-all-pedidos');
        },
        changePage(){
            this.$emit('change-page', this.paginator.page);            
        },
        synchronizeOrders(){
            this.$emit('synchronize-orders');
        },
        downloadExcel(){
            this.$emit('download-excel');
        },
        // Métodos para el modal de tracking
        openTrackingInNewWindow(url) {
            window.open(url, '_blank', 'noopener,noreferrer');
        },
    }
};

</script>
<style scoped>

</style>